import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProgressChart from './components/ProgressChart';
import GoalCard from './components/GoalCard';
import AchievementBadge from './components/AchievementBadge';
import WellnessMetrics from './components/WellnessMetrics';
import MilestoneTimeline from './components/MilestoneTimeline';
import InsightsPanel from './components/InsightsPanel';

const ProgressTracking = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [showNewGoalModal, setShowNewGoalModal] = useState(false);

  // Mock user data
  const user = {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    tier: "All Access",
    joinDate: "2024-01-15",
    totalPoints: 2450,
    currentStreak: 12,
    level: 8
  };

  // Mock notifications
  const notifications = [
    {
      id: 1,
      title: "Goal Achievement!",
      message: "You\'ve completed your weekly fitness goal",
      time: "2 hours ago",
      read: false
    },
    {
      id: 2,
      title: "New Badge Earned",
      message: "Mindfulness Master badge unlocked",
      time: "1 day ago",
      read: false
    }
  ];

  // Mock progress chart data
  const progressChartData = [
    { name: 'Mon', value: 85, date: '2024-10-01' },
    { name: 'Tue', value: 92, date: '2024-10-02' },
    { name: 'Wed', value: 78, date: '2024-10-03' },
    { name: 'Thu', value: 95, date: '2024-10-04' },
    { name: 'Fri', value: 88, date: '2024-10-05' },
    { name: 'Sat', value: 90, date: '2024-10-06' },
    { name: 'Sun', value: 87, date: '2024-10-07' }
  ];

  const wellnessDistributionData = [
    { name: 'Fitness', value: 35 },
    { name: 'Nutrition', value: 25 },
    { name: 'Mindfulness', value: 20 },
    { name: 'Sleep', value: 15 },
    { name: 'Community', value: 5 }
  ];

  // Mock goals data
  const [goals, setGoals] = useState([
    {
      id: 1,
      title: "Daily Step Challenge",
      description: "Walk 10,000 steps every day for better cardiovascular health",
      category: "fitness",
      progress: 85,
      currentValue: 8500,
      targetValue: 10000,
      unit: "steps",
      status: "in-progress",
      startDate: "2024-09-15",
      targetDate: "2024-10-15",
      streak: 12,
      milestones: [
        { title: "First 5,000 steps", completed: true },
        { title: "Reach 7,500 steps", completed: true },
        { title: "Hit 10,000 steps", completed: false }
      ]
    },
    {
      id: 2,
      title: "Mindful Meditation",
      description: "Practice daily meditation for mental clarity and stress reduction",
      category: "mindfulness",
      progress: 70,
      currentValue: 14,
      targetValue: 20,
      unit: "sessions",
      status: "in-progress",
      startDate: "2024-09-01",
      targetDate: "2024-10-31",
      streak: 7,
      milestones: [
        { title: "Complete 5 sessions", completed: true },
        { title: "Reach 10 sessions", completed: true },
        { title: "Achieve 20 sessions", completed: false }
      ]
    },
    {
      id: 3,
      title: "Healthy Nutrition Plan",
      description: "Follow a balanced diet with proper macro distribution",
      category: "nutrition",
      progress: 92,
      currentValue: 23,
      targetValue: 25,
      unit: "days",
      status: "in-progress",
      startDate: "2024-09-10",
      targetDate: "2024-10-10",
      streak: 8,
      milestones: [
        { title: "Complete first week", completed: true },
        { title: "Reach 15 days", completed: true },
        { title: "Finish 25 days", completed: false }
      ]
    }
  ]);

  // Mock achievements data
  const achievements = [
    {
      id: 1,
      title: "Fitness Enthusiast",
      description: "Completed 30 workout sessions",
      category: "fitness",
      icon: "Dumbbell",
      earnedDate: "2024-10-05",
      points: 150,
      rarity: "common",
      isNew: true
    },
    {
      id: 2,
      title: "Mindfulness Master",
      description: "Meditated for 100 consecutive days",
      category: "mindfulness",
      icon: "Brain",
      earnedDate: "2024-10-03",
      points: 300,
      rarity: "rare",
      level: 3,
      isNew: false
    },
    {
      id: 3,
      title: "Community Champion",
      description: "Helped 50 community members",
      category: "community",
      icon: "Users",
      earnedDate: "2024-10-01",
      points: 200,
      rarity: "uncommon",
      isNew: false
    }
  ];

  // Mock wellness metrics
  const [wellnessMetrics, setWellnessMetrics] = useState([
    {
      id: 1,
      type: "steps",
      current: 8500,
      target: 10000,
      unit: "steps",
      lastUpdated: "2 hours ago",
      streak: 12
    },
    {
      id: 2,
      type: "water",
      current: 6,
      target: 8,
      unit: "glasses",
      lastUpdated: "1 hour ago",
      streak: 5
    },
    {
      id: 3,
      type: "sleep",
      current: 7.5,
      target: 8,
      unit: "hours",
      lastUpdated: "8 hours ago",
      streak: 3
    },
    {
      id: 4,
      type: "meditation",
      current: 15,
      target: 20,
      unit: "minutes",
      lastUpdated: "3 hours ago",
      streak: 7
    }
  ]);

  // Mock milestones data
  const milestones = [
    {
      id: 1,
      title: "First Week Complete!",
      description: "Successfully completed your first week of wellness tracking",
      type: "goal-completed",
      date: "2024-10-07",
      points: 100,
      category: "general",
      isNew: true,
      celebrated: false,
      details: "You\'ve established a solid foundation for your wellness journey by consistently tracking your progress for 7 days."
    },
    {
      id: 2,
      title: "Meditation Streak Master",
      description: "Achieved 7-day meditation streak",
      type: "streak",
      date: "2024-10-06",
      points: 150,
      category: "mindfulness",
      isNew: false,
      celebrated: true,
      details: "Your dedication to daily meditation is paying off with improved focus and reduced stress levels."
    },
    {
      id: 3,
      title: "Fitness Goal Achieved",
      description: "Completed your monthly fitness challenge",
      type: "achievement",
      date: "2024-10-05",
      points: 200,
      category: "fitness",
      isNew: false,
      celebrated: true,
      details: "You\'ve successfully met your fitness targets and improved your overall physical health."
    }
  ];

  // Mock insights data
  const insights = [
    {
      id: 1,
      title: "Consistent Progress Trend",
      description: "Your wellness scores have improved by 15% over the past month",
      type: "trend",
      date: "2024-10-07",
      category: "general",
      impact: "high",
      details: "Your consistent daily tracking and goal completion has resulted in significant improvement across all wellness categories.",
      actionItems: [
        "Continue current routine",
        "Consider setting more challenging goals",
        "Share your success with the community"
      ],
      actionable: true
    },
    {
      id: 2,
      title: "Sleep Pattern Analysis",
      description: "Your sleep quality correlates with meditation practice",
      type: "pattern",
      date: "2024-10-06",
      category: "sleep",
      impact: "medium",
      details: "Data shows that on days when you meditate, your sleep quality improves by an average of 20%.",
      actionItems: [
        "Maintain evening meditation routine",
        "Track sleep quality metrics",
        "Consider extending meditation duration"
      ],
      actionable: true
    }
  ];

  // Mock recommendations data
  const recommendations = [
    {
      id: 1,
      title: "Increase Water Intake",
      description: "You\'re consistently 1-2 glasses short of your daily water goal",
      priority: "medium",
      estimatedTime: "5 min setup",
      difficulty: "easy",
      category: "nutrition"
    },
    {
      id: 2,
      title: "Add Strength Training",
      description: "Consider adding 2 strength training sessions per week",
      priority: "low",
      estimatedTime: "30 min/session",
      difficulty: "medium",
      category: "fitness"
    },
    {
      id: 3,
      title: "Evening Wind-down Routine",
      description: "Create a consistent bedtime routine to improve sleep quality",
      priority: "high",
      estimatedTime: "15 min daily",
      difficulty: "easy",
      category: "sleep"
    }
  ];

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleProfileClick = () => {
    console.log('Profile clicked');
  };

  const handleEditGoal = (goal) => {
    console.log('Edit goal:', goal);
  };

  const handleCompleteGoal = (goal) => {
    setGoals(goals?.map(g => 
      g?.id === goal?.id 
        ? { ...g, status: 'completed', progress: 100 }
        : g
    ));
  };

  const handleDeleteGoal = (goalId) => {
    setGoals(goals?.filter(g => g?.id !== goalId));
  };

  const handleShareAchievement = (achievement) => {
    console.log('Share achievement:', achievement);
  };

  const handleUpdateMetric = (type, action = 'save') => {
    console.log('Update metric:', type, action);
    // Update metric logic here
  };

  const handleCelebrateMilestone = (milestone) => {
    console.log('Celebrate milestone:', milestone);
  };

  const handleShareMilestone = (milestone) => {
    console.log('Share milestone:', milestone);
  };

  const handleApplyRecommendation = (recommendation, action) => {
    console.log('Apply recommendation:', recommendation, action);
  };

  const viewOptions = [
    { value: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { value: 'goals', label: 'Goals', icon: 'Target' },
    { value: 'achievements', label: 'Achievements', icon: 'Award' },
    { value: 'metrics', label: 'Metrics', icon: 'BarChart3' },
    { value: 'insights', label: 'Insights', icon: 'Lightbulb' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onProfileClick={handleProfileClick}
      />
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        user={user}
      />
      <main className={`transition-all duration-300 ${
        isSidebarCollapsed ? 'ml-16' : 'ml-64'
      } pt-16`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Progress Tracking
              </h1>
              <p className="text-muted-foreground">
                Monitor your wellness journey and celebrate achievements
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* View Toggle */}
              <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                {viewOptions?.map((option) => (
                  <Button
                    key={option?.value}
                    variant={activeView === option?.value ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveView(option?.value)}
                    iconName={option?.icon}
                    iconPosition="left"
                  >
                    {option?.label}
                  </Button>
                ))}
              </div>
              
              <Button
                variant="default"
                onClick={() => setShowNewGoalModal(true)}
                iconName="Plus"
                iconPosition="left"
              >
                New Goal
              </Button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Target" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-foreground">{goals?.length}</p>
                  <p className="text-sm text-muted-foreground">Active Goals</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Award" size={24} className="text-success" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-foreground">{achievements?.length}</p>
                  <p className="text-sm text-muted-foreground">Achievements</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Flame" size={24} className="text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-foreground">{user?.currentStreak}</p>
                  <p className="text-sm text-muted-foreground">Day Streak</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Star" size={24} className="text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-foreground">{user?.totalPoints}</p>
                  <p className="text-sm text-muted-foreground">Total Points</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Based on Active View */}
          {activeView === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <ProgressChart 
                  data={progressChartData}
                  title="Weekly Progress Overview"
                  type="line"
                />
                <WellnessMetrics 
                  metrics={wellnessMetrics}
                  onUpdateMetric={handleUpdateMetric}
                />
              </div>
              
              <div className="space-y-6">
                <ProgressChart 
                  data={wellnessDistributionData}
                  title="Wellness Category Distribution"
                  type="pie"
                />
                <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                    Recent Achievements
                  </h3>
                  <div className="space-y-4">
                    {achievements?.slice(0, 3)?.map((achievement) => (
                      <AchievementBadge
                        key={achievement?.id}
                        achievement={achievement}
                        showDetails={true}
                        onShare={handleShareAchievement}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'goals' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {goals?.map((goal) => (
                <GoalCard
                  key={goal?.id}
                  goal={goal}
                  onEdit={handleEditGoal}
                  onComplete={handleCompleteGoal}
                  onDelete={handleDeleteGoal}
                />
              ))}
            </div>
          )}

          {activeView === 'achievements' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {achievements?.map((achievement) => (
                  <AchievementBadge
                    key={achievement?.id}
                    achievement={achievement}
                    size="large"
                    onShare={handleShareAchievement}
                  />
                ))}
              </div>
              
              <MilestoneTimeline
                milestones={milestones}
                onCelebrate={handleCelebrateMilestone}
                onShare={handleShareMilestone}
              />
            </div>
          )}

          {activeView === 'metrics' && (
            <div className="space-y-6">
              <WellnessMetrics 
                metrics={wellnessMetrics}
                onUpdateMetric={handleUpdateMetric}
              />
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ProgressChart 
                  data={progressChartData}
                  title="Daily Wellness Score"
                  type="line"
                />
                <ProgressChart 
                  data={wellnessDistributionData}
                  title="Category Breakdown"
                  type="bar"
                />
              </div>
            </div>
          )}

          {activeView === 'insights' && (
            <div className="space-y-6">
              <InsightsPanel
                insights={insights}
                recommendations={recommendations}
                onApplyRecommendation={handleApplyRecommendation}
              />
            </div>
          )}
        </div>
      </main>
      {/* New Goal Modal */}
      {showNewGoalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-md shadow-wellness-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-foreground">Create New Goal</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowNewGoalModal(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { category: 'fitness', label: 'Fitness', icon: 'Dumbbell', color: 'success' },
                  { category: 'nutrition', label: 'Nutrition', icon: 'Apple', color: 'accent' },
                  { category: 'mindfulness', label: 'Mindfulness', icon: 'Brain', color: 'primary' },
                  { category: 'community', label: 'Community', icon: 'Users', color: 'secondary' }
                ]?.map((cat) => (
                  <Button
                    key={cat?.category}
                    variant="outline"
                    className="h-20 flex flex-col items-center space-y-2"
                    onClick={() => {
                      console.log('Selected category:', cat?.category);
                      setShowNewGoalModal(false);
                    }}
                  >
                    <Icon name={cat?.icon} size={24} className={`text-${cat?.color}`} />
                    <span className="text-sm">{cat?.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressTracking;